import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, Clock, MessageCircle, Zap, Target } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";
const EMAIL = "hello@kalzastudio.com";

const OrganicImage = ({ src, alt, className = "" }) => (
  <div className={`aspect-[4/5] overflow-hidden max-w-[65%] lg:max-w-none ${className}`} style={{ WebkitMaskImage: 'url(/images/mood/shape-mask.png)', maskImage: 'url(/images/mood/shape-mask.png)', WebkitMaskSize: '100% 100%', maskSize: '100% 100%', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat' }}>
    <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
  </div>
);

export default function ConsultationPagePL() {
  return (
    <main data-testid="consultation-page-pl">

      {/* 1. HERO */}
      <section className="pt-32 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/75" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">SZYBKA ROZMOWA</span>
            <h1 className="mb-6">
              <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">KONSULTACJA 1:1</span>
              <span className="font-accent text-2xl md:text-3xl lg:text-4xl text-[#BE123C] block leading-[1.1] mt-2">90 minut na to, co teraz blokuje Twoją markę</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-xl mb-10">
              Ty przynoszisz pytanie. Ja przynoszę 15 lat strategii marki, kierowania kreatywnymi zespołami 40+ osób w całej Europie i prowadzenia założycieli przez pivoty, decyzje cenowe i kryzysy tożsamości. Siadamy na 90 minut i pracujemy nad tym, co utknęło. Wychodzisz z jasnością i konkretnym następnym krokiem.
            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              {["90 minut", "Rozmowa wideo", "Nagranie w cenie", "Notatki po rozmowie"].map((label, i) => (
                <div key={i} className="flex items-center gap-2.5 bg-white/5 border border-white/10 px-4 py-2">
                  <Check size={12} className="text-[#D4A0A0]" />
                  <span className="font-body text-xs text-white/60 tracking-wide">{label}</span>
                </div>
              ))}
            </div>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
              UMÓW KONSULTACJĘ <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. PRAWDZIWY PROBLEM */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">PRAWDZIWY PROBLEM</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">WIESZ, ŻE COŚ NIE GRA</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">ale nie potrafisz tego jeszcze nazwać</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-lg">
                <p>Od tygodni myślisz o swojej marce. Może od miesięcy. Wiesz, że coś trzeba zmienić, ale nie potrafisz wskazać co. Przeskakujesz między pomysłami, czytasz artykuły, pytasz znajomych o zdanie i kończysz bardziej zdezorientowana niż na początku.</p>
                <p>Czasami wystarczy 90 minut z kimś, kto widział setki marek od środka. Kimś, kto potrafi nazwać, co jest nie tak, w pierwszych 15 minutach i resztę czasu poświęcić na to, co z tym zrobić. Do tego służy ta rozmowa.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center">
              <OrganicImage src="/images/ola-consultation.jpg" alt="Aleksandra Kałuża" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. Z CZYM PRZYCHODZĄ ZAŁOŻYCIELE */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">CZĘSTE TEMATY</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">Z CZYM ZAŁOŻYCIELE</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">przychodzą na te rozmowy</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { Icon: Target, title: "Pozycjonowanie jest niejasne", body: "Wiesz, co robisz, ale nie potrafisz tego wytłumaczyć tak, żeby ludzie się zainteresowali. Znajdziemy to jedno zdanie, które trafia." },
              { Icon: MessageCircle, title: "Komunikacja brzmi generycznie", body: "Twoja strona i Instagram brzmią jak każdy inny biznes w Twojej niszy. Ustalimy, co naprawdę Cię wyróżnia i jak to powiedzieć." },
              { Icon: Zap, title: "Coś nie gra, ale nie potrafisz tego nazwać", body: "Marka wygląda ok na papierze, ale coś nie klika. Zazwyczaj potrafię to zobaczyć w pierwszych 15 minutach." },
              { Icon: Clock, title: "Stoisz przed dużą decyzją", body: "Rebranding, pivot, podwyżka cen, nowa oferta. Chcesz, żeby ktoś z doświadczeniem na to spojrzał, zanim się zaangażujesz." },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex items-start gap-4 bg-white p-6 border border-[#e5e2de]">
                <item.Icon size={20} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-body text-sm font-semibold text-[#1C1210] mb-1">{item.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{item.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. JAK TO DZIAŁA */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">JAK TO DZIAŁA</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">PROSTY PROCES</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">w trzech krokach</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              { step: "01", title: "Umów rozmowę", body: "Wybierz termin, który Ci pasuje. Przed rozmową poproszę Cię o wypełnienie krótkiego formularza, żebym przyszła przygotowana." },
              { step: "02", title: "Rozmawiamy 90 minut", body: "Wchodzimy w prawdziwy problem. Słucham, zadaję pytania, mówię co widzę. Pracujemy nad tym razem i dochodzimy do jasnego kierunku." },
              { step: "03", title: "Dostajesz nagranie i notatki", body: "Po rozmowie otrzymujesz nagranie i moje pisemne podsumowanie z konkretnymi następnymi krokami. Bez lania wody, tylko co zrobić." },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="border-t border-[#1C1210]/15 pt-6">
                <span className="font-display text-4xl text-[#BE123C] block mb-4 leading-none">{s.step}</span>
                <h3 className="font-serif text-xl text-[#1C1210] mb-3">{s.title}</h3>
                <p className="font-body text-sm text-[#6B6560] leading-[1.7]">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. CZY TO DLA CIEBIE */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">CZY TO DLA CIEBIE</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 border border-[#e5e2de]">
                <h3 className="font-serif text-lg text-[#1C1210] mb-4">Ta rozmowa jest dla Ciebie, jeśli</h3>
                <ul className="space-y-3">
                  {[
                    "Masz konkretne pytanie o markę, które wymaga doświadczonego oka",
                    "Chcesz mieć jasność, zanim zainwestujesz w większy projekt",
                    "Utknęłaś przy jakiejś decyzji i potrzebujesz kogoś, kto pomoże Ci ją przemyśleć",
                    "Chcesz szczerej opinii o obecnej prezencji Twojej marki",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={14} className="text-[#BE123C] flex-shrink-0 mt-1" />
                      <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white p-6 border border-[#e5e2de]">
                <h3 className="font-serif text-lg text-[#1C1210] mb-4">To nie będzie dobre dopasowanie, jeśli</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#9A9590] flex-shrink-0 mt-2" />
                    <span className="font-body text-sm text-[#6B6560] leading-relaxed">Potrzebujesz pełnej strategii marki. Zerknij na <Link to="/pl/brand-foundation" className="text-[#BE123C] underline hover:text-[#9F1239]">Brand Foundation</Link>.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#9A9590] flex-shrink-0 mt-2" />
                    <span className="font-body text-sm text-[#6B6560] leading-relaxed">Potrzebujesz materiałów wizualnych. Zerknij na <Link to="/pl/visual-identity" className="text-[#BE123C] underline hover:text-[#9F1239]">Pełny System Marki</Link>.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#9A9590] flex-shrink-0 mt-2" />
                    <span className="font-body text-sm text-[#6B6560] leading-relaxed">Szukasz regularnego coachingu. Prowadzę pojedyncze konsultacje, nie cykliczne sesje.</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="bg-[#f5f5f7] p-8 md:p-12 relative border border-[#e5e2de]">
              <span className="absolute left-6 top-4 text-7xl font-serif text-[#BE123C]/10 select-none pointer-events-none leading-none">&ldquo;</span>
              <div className="relative space-y-4 font-body text-base md:text-lg text-[#4A4540] leading-relaxed max-w-3xl">
                <p>Wcześniej nie widziałem zbyt dużej wartości w tym, co robię. Przychodziłem do pracy dla wypłaty, nie dla ludzi.</p>
                <p>Te warsztaty pokazały mi, co faktycznie oferuję, jakie są moje prawdziwe mocne strony i co naprawdę chcę reprezentować. Moim atutem nigdy nie była wiedza techniczna. To była moja osobowość i sposób, w jaki podchodzę do ludzi.</p>
                <p>Praca znów ma kolor. Teraz budzę się z chęcią do pracy, a nie czułem tego od dłuższego czasu.</p>
              </div>
              <p className="font-body text-sm text-[#9A9590] mt-8 tracking-wider uppercase">Grzegorz Galikowski, Finansy Founder</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. FINAL CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">KIEDY BĘDZIESZ GOTOWA</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">90 MINUT</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-2">może zmienić kierunek Twojej marki</span>
            </h2>
            <p className="font-body text-base text-white/70 leading-relaxed mb-10">
              Nie musisz mieć wszystkiego poukładanego. Wystarczy jedna dobra rozmowa z kimś, kto wie, na co patrzeć. Umów termin i wchodzimy w temat.
            </p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
              UMÓW KONSULTACJĘ <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
